import React, { useState } from 'react';
import { CountdownTimer } from '../components/CountdownTimer';
import { TestimonialCarousel } from '../components/TestimonialCarousel';
import { SocialProofPopup } from '../components/SocialProofPopup';
import { useGeoPrice } from '../hooks/useGeoPrice';
import { trackPixelEvent } from '../utils/metaPixel';
import { CheckCircle2, ChevronRight, Clock, ShieldCheck, Download, Smartphone, Check, PlaneTakeoff, Brain, HeartHandshake, Headphones, BookOpen, FileText, ListChecks } from 'lucide-react';
const IMAGES = {
  logo: 'https://cdn.magicpatterns.com/uploads/gunxqmX3DLE3VayEChcB9z/LOGO_ATLAS.png',
  aerofobia: 'https://cdn.magicpatterns.com/uploads/1mVdpFchd5giBM25U8BsiK/AEROFOBIA.png',
  checklist: 'https://cdn.magicpatterns.com/uploads/wDAogEQctEEugt1Ckfp8e1/CHECKLIST.png',
  cuaderno: 'https://cdn.magicpatterns.com/uploads/8TaniZ9QhgVHdEYDseeY6z/Cuaderno_de_ejercicios.png',
  guiaSos: 'https://cdn.magicpatterns.com/uploads/eGWxKbZFeAThtG1LpDP8Pk/GUIA_SOS.png',
  biblioteca: 'https://cdn.magicpatterns.com/uploads/7NE7uCYZUcEmekCjGdKWyc/BIBLIOTECA_DE_CALMA_EN_VUELO.png',
  psicologa: 'https://cdn.magicpatterns.com/uploads/2aK8MAhuP1aa9XVLrcf5Cz/ChatGPT_Image_24_ago_2026,_21_23_31.png'
};
const FAQS = [{
  q: '¿Esto elimina el miedo a volar?',
  a: 'No prometemos eliminar el miedo de un día para el otro. Te da herramientas prácticas para entenderlo, regular la ansiedad y atravesar el vuelo con más calma.'
}, {
  q: '¿Cuándo recibo el acceso?',
  a: 'El acceso es inmediato. Podés empezar a leer, escuchar y completar los ejercicios apenas recibís el material.'
}, {
  q: '¿Puedo usarlo durante el vuelo?',
  a: 'Sí. La Guía SOS está pensada para leer en el avión y los audios pueden escucharse con auriculares sin conexión (si los descargás antes).'
}, {
  q: '¿Los audios necesitan internet?',
  a: 'Recomendamos descargarlos antes del viaje para poder escucharlos incluso si no tenés conexión durante el vuelo.'
}, {
  q: '¿Es un tratamiento psicológico?',
  a: 'No. Es un recurso educativo y de acompañamiento. No reemplaza atención médica ni tratamiento psicológico profesional.'
}];
const CART_URL = 'https://atlas-de-viajes.impultienda.ar/checkout/aerofobia-guia-para-volar-en-calma';
export function Landing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const {
    price,
    isLoading: isPriceLoading
  } = useGeoPrice();
  const scrollToBuy = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const buyNow = () => {
    trackPixelEvent('InitiateCheckout', {
      content_name: 'Aerofobia: Guía para Volar en Calma',
      currency: price.currency,
      value: price.amount
    });
    window.location.href = CART_URL;
  };
  return <div className="min-h-screen bg-polar font-sans text-oceano" data-id="element-31" data-mp="v6j93qfd6hba">
      <SocialProofPopup data-id="element-32" data-mp="2li7dij1mastw" />

      {/* SECCIÓN 1 — Barra superior */}
      <div className="fixed top-0 left-0 right-0 z-50" data-id="element-33" data-mp="2piznxfm873rr">
        <div className="bg-oceano text-polar text-[10px] md:text-sm py-1.5 md:py-2 text-center font-medium tracking-wide" data-id="element-34" data-mp="2phohf81ebhwy">
          Producto 100% digital · Acceso inmediato
        </div>
        <div className="bg-travertino text-oceano text-xs md:text-sm py-2 text-center font-medium flex items-center justify-center gap-2 shadow-sm" data-id="element-35" data-mp="9v3wfbot0647">
          <Clock size={16} className="text-oceano" data-id="element-36" data-mp="iuh3jxpggilr" />
          <span data-id="element-37" data-mp="iswaydmpb9qj">Oferta por tiempo limitado · termina en:</span>
          <CountdownTimer initialMinutes={20} data-id="element-38" data-mp="xibzdmyrf332" />
        </div>
      </div>

      {/* HERO MOBILE-FIRST: logo → título/subtítulo → imagen → texto */}
      <section className="pt-28 pb-12 md:pt-40 md:pb-20 px-4 max-w-3xl mx-auto flex flex-col items-center text-center space-y-6 md:space-y-8" data-id="element-39" data-mp="sm4qblb2hbgw">
        {/* 1. Logo */}
        <img src={IMAGES.logo} alt="Atlas de Viajes" className="h-24 md:h-32 object-contain" data-id="element-40" data-mp="11m08wb6yc8qy" />

        {/* 2. Título y subtítulo */}
        <div data-id="element-41" data-mp="38d97a5jzqpob">
          <h1 className="font-cormorant text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight mb-4" data-id="element-42" data-mp="33c3bfyv1amak">
            No dejes que el <span className="text-caribe" data-id="element-43" data-mp="35ri620hoh267">miedo</span> decida tu
            próximo viaje
          </h1>
          <p className="text-lg md:text-2xl text-hierro font-medium leading-snug" data-id="element-44" data-mp="39rk8ibv8lzmj">
            Un método práctico para calmar la ansiedad, entender lo que pasa en
            el avión y vivir tu próximo vuelo con más tranquilidad.
          </p>
        </div>

        {/* 3. Imagen */}
        <div className="w-full max-w-sm mx-auto relative mt-2" data-id="element-45" data-mp="2m39ffuvs948t">
          <div className="absolute -top-4 -right-4 w-28 h-28 bg-travertino/20 rounded-full blur-2xl" data-id="element-46" data-mp="2m0ocf2jr0i9h"></div>
          <div className="absolute -bottom-4 -left-4 w-28 h-28 bg-caribe/20 rounded-full blur-2xl" data-id="element-47" data-mp="2qzhnkobkbx6h"></div>
          <img src={IMAGES.aerofobia} alt="Mockup Aerofobia" className="relative z-10 w-full h-auto rounded-2xl shadow-xl object-contain" data-id="element-48" data-mp="30rnervtzqakm" />
        </div>

        {/* 4. Texto y todo */}

        <ul className="space-y-3 w-full max-w-md text-left" data-id="element-49" data-mp="23a5fjrrz1v5r">
          {['Para ansiedad antes del vuelo.', 'Para miedo a turbulencias y ruidos.', 'Para usar en el aeropuerto y en el avión.', 'Incluye guía, audios, ejercicios y checklist.'].map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-50" data-mp="1vts6tump6z8u" data-mpi={i}>
              <CheckCircle2 className="text-caribe shrink-0 mt-0.5" size={20} data-id="element-51" data-mp="1qtnp61a1zyh1" />
              <span className="text-oceano font-medium" data-id="element-52" data-mp="1qvqoxd10d5yy">{bullet}</span>
            </li>)}
        </ul>

        <div className="w-full max-w-md pt-2" data-id="element-53" data-mp="1lwngdiltkhm8">
          <button onClick={scrollToBuy} className="w-full bg-caribe hover:bg-[#24a8a2] text-polar px-8 py-4 md:px-10 md:py-5 rounded-xl font-semibold text-lg md:text-xl transition-colors shadow-lg shadow-caribe/20 flex items-center justify-center gap-2" data-id="element-54" data-mp="1ht7muv51qxdr">
            Quiero volar con más calma
          </button>
          <p className="text-xs md:text-sm text-hierro flex items-center justify-center gap-2 mt-4" data-id="element-55" data-mp="1354fpif9pfnd">
            Acceso inmediato · Producto digital
          </p>
        </div>
      </section>

      {/* SECCIÓN DE DOLOR MÁS HUMANA */}
      <section className="bg-oceano text-polar py-16 md:py-20 px-4" data-id="element-56" data-mp="u5hotqk5zs8k">
        <div className="max-w-4xl mx-auto" data-id="element-57" data-mp="jhi2bg71gula">
          <h2 className="font-cormorant text-3xl md:text-5xl font-semibold mb-6 text-center md:text-left" data-id="element-58" data-mp="jkmaeonmkah8">
            ¿Sentís que el miedo empieza mucho antes del vuelo?
          </h2>
          

          <div className="grid sm:grid-cols-2 gap-4 mb-12" data-id="element-59" data-mp="ajxvzsubk83t">
            {['Te cuesta dormir la noche anterior.', 'Pensás "¿y si me agarra ansiedad en el avión?"', 'Cada ruido te hace imaginar lo peor.', 'La turbulencia te activa el cuerpo.', 'Sentís que no tener control es lo más difícil.', 'Querés viajar, pero el miedo te frena.'].map((q, i) => <div key={i} className="bg-polar/5 p-5 rounded-xl border border-polar/10 flex items-start gap-3" data-id="element-60" data-mp="th8wthvln9ld" data-mpi={i}>
                <div className="w-1.5 h-1.5 rounded-full bg-travertino mt-2 shrink-0" data-id="element-61" data-mp="ye941w46qie2"></div>
                <p className="text-polar/90 font-medium" data-id="element-62" data-mp="1m43fc98uvyw7">{q}</p>
              </div>)}
          </div>

          <div className="bg-travertino/10 border border-travertino/20 p-6 rounded-2xl text-center mb-8" data-id="element-63" data-mp="1qwwjauwi3378">
            <p className="font-cormorant text-2xl md:text-3xl text-travertino italic" data-id="element-64" data-mp="209c4twz4dmwt">
              "El problema no es que no quieras viajar. Es que no querés volver
              a sufrir todo el vuelo."
            </p>
          </div>

          <p className="text-center text-polar/90 text-lg" data-id="element-65" data-mp="2a2at1dqpfbfw">
            Por eso este método no te habla desde la teoría. Te da herramientas
            simples para saber qué hacer cuando el miedo aparece.
          </p>
        </div>
      </section>

      {/* SECCIÓN PUENTE / NUEVA CREENCIA */}
      <section className="py-16 md:py-20 px-4 bg-polar" data-id="element-66" data-mp="1dcaa3vd0np6i">
        <div className="max-w-5xl mx-auto text-center" data-id="element-67" data-mp="1rb02wyssz2p2">
          <h2 className="font-cormorant text-3xl md:text-5xl font-semibold text-oceano mb-6" data-id="element-68" data-mp="1mavm17nzg5yb">
            No necesitás controlar el avión. Necesitás un plan para tu ansiedad.
          </h2>
          <p className="text-lg md:text-xl text-hierro mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-69" data-mp="32qylbt9vf12y">
            Cuando entendés qué pasa en tu cuerpo, qué cosas son normales
            durante el vuelo y qué hacer en los momentos difíciles, el miedo
            deja de sentirse tan gigante.
          </p>

          <p className="font-semibold text-oceano text-xl mb-8" data-id="element-70" data-mp="151jmdlzqp7em">
            El Método Volar en Calma trabaja en 3 pasos:
          </p>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-71" data-mp="19sjlmxwc01ka">
            {[{
            title: 'Entender',
            desc: 'Qué es la aerofobia, por qué aparece la ansiedad y qué significan muchas sensaciones del vuelo.',
            icon: <Brain className="text-caribe" size={32} data-id="element-72" data-mp="1jowc7mwkxzat" />
          }, {
            title: 'Preparar',
            desc: 'Ejercicios, checklist y recursos para llegar al aeropuerto con más claridad.',
            icon: <PlaneTakeoff className="text-caribe" size={32} data-id="element-73" data-mp="1sqcm7tjhfzei" />
          }, {
            title: 'Acompañar',
            desc: 'Guía SOS, audios y meditaciones para usar antes y durante el vuelo.',
            icon: <HeartHandshake className="text-caribe" size={32} data-id="element-74" data-mp="3uir08hiwojpn" />
          }].map((pillar, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-travertino/20 text-center flex flex-col items-center" data-id="element-75" data-mp="3n0khcv3t8lfz" data-mpi={i}>
                <div className="w-16 h-16 bg-caribe/10 rounded-full flex items-center justify-center mb-4" data-id="element-76" data-mp="3d96cs8x04vfz">
                  {pillar.icon}
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-3" data-id="element-77" data-mp="38bod794vjskn">
                  {pillar.title}
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-78" data-mp="4tmlvgjszp43">
                  {pillar.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* MIRADA PROFESIONAL */}
      <section className="py-16 md:py-20 px-4 bg-[#F5F5F0]" data-id="element-79" data-mp="3rym0th55emrz">
        <div className="max-w-5xl mx-auto grid gap-8 md:grid-cols-[minmax(0,22rem)_1fr] md:gap-12 md:items-center" data-id="element-80" data-mp="3s0f88ynl5b11">
          <div data-id="element-81" data-mp="2gixfeu7jlvta">
            <img src={IMAGES.psicologa} alt="Dra. Lucía Ferrer, psicóloga especializada en ansiedad y fobias" className="w-full max-w-xs mx-auto md:max-w-none h-auto rounded-2xl shadow-lg object-cover" loading="lazy" data-id="element-82" data-mp="2gie4vyk829pa" />
          </div>

          <div data-id="element-83" data-mp="emwrmn9llku0">
            <h2 className="font-cormorant text-3xl md:text-4xl lg:text-5xl font-semibold text-oceano leading-tight" data-id="element-84" data-mp="ej8xblwbwue8">
              Una mirada profesional sobre el miedo a volar
            </h2>

            <div className="mt-5 pb-5 border-b border-travertino/30" data-id="element-85" data-mp="nkp9nz6pf6l3">
              <p className="font-semibold text-oceano text-lg md:text-xl" data-id="element-86" data-mp="129v2n5ey80kj">
                Dra. Lucía Ferrer
              </p>
              <p className="text-hierro text-sm md:text-base" data-id="element-87" data-mp="xbjy9a7fai9p">
                Psicóloga especializada en ansiedad y fobias.
              </p>
            </div>

            <div className="mt-6 space-y-4 text-base md:text-lg text-hierro leading-relaxed" data-id="element-88" data-mp="16c9lhpimb0bt">
              <p data-id="element-89" data-mp="3d264jssxtfpo">
                El miedo a volar no siempre desaparece simplemente entendiendo
                que el avión es seguro. Muchas veces es necesario aprender a
                reconocer qué activa la ansiedad y contar con herramientas
                concretas para atravesar esos momentos con mayor calma.
              </p>
              <p data-id="element-90" data-mp="1sk5allknxssm">
                Trabajar la aerofobia implica comprender el miedo, anticiparse a
                los momentos más difíciles y desarrollar recursos que ayuden a
                recuperar sensación de control antes y durante el vuelo.
              </p>
              <p className="text-oceano font-medium" data-id="element-91" data-mp="14uayx7c2ya9y">
                Prepararte antes de viajar puede cambiar por completo la forma
                en la que enfrentás tu próximo vuelo. Y yo estoy para ayudarte.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* QUÉ INCLUYE EL MÉTODO */}
      <section className="py-16 md:py-20 px-4 bg-[#F5F5F0]" data-id="element-92" data-mp="zwjawoek38b1">
        <div className="max-w-6xl mx-auto" data-id="element-93" data-mp="2q8fgwcrhf2pj">
          <div className="text-center mb-12" data-id="element-94" data-mp="2q6ksupkwyuh8">
            <h2 className="font-cormorant text-4xl md:text-5xl font-semibold text-oceano mb-4" data-id="element-95" data-mp="2uak7f2mmvj7y">
              ¿Qué incluye el método?
            </h2>
          </div>

          <div className="flex flex-col gap-8 md:grid md:grid-cols-2 lg:grid-cols-3" data-id="element-96" data-mp="6zgu4wbbbafs">
            {/* Card 1 */}
            <div className="bg-white rounded-3xl overflow-hidden border border-travertino/20 shadow-sm flex flex-col" data-id="element-97" data-mp="3u69d04fvye7z">
              <div className="p-6 bg-polar flex justify-center items-center h-64 md:h-72" data-id="element-98" data-mp="3p87wnr9ugt07">
                <img src={IMAGES.aerofobia} alt="Ebook" className="max-w-full max-h-full object-contain drop-shadow-xl" data-id="element-99" data-mp="psue66dhlwgm" />
              </div>
              <div className="p-6 md:p-8 flex-1 border-t border-travertino/10" data-id="element-100" data-mp="lr94etw91yan">
                <div className="flex items-center gap-2 mb-3" data-id="element-101" data-mp="gypuc6wrtlal">
                  <BookOpen size={18} className="text-caribe" data-id="element-102" data-mp="c1622338n7zn" />
                  <span className="text-xs font-bold text-caribe uppercase tracking-wider" data-id="element-103" data-mp="bytpk5g91ptz">
                    Ebook principal
                  </span>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-2" data-id="element-104" data-mp="14otn1j8f80mt">
                  Aerofobia: Guía para Volar en Calma
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-105" data-mp="1gzb4a42lzcmx">
                  La guía base para entender el miedo, conocer qué pasa en el
                  avión y aprender herramientas para viajar con más
                  tranquilidad.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-3xl overflow-hidden border border-travertino/20 shadow-sm flex flex-col" data-id="element-106" data-mp="1uysxqwqune2m">
              <div className="p-6 bg-polar flex justify-center items-center h-64 md:h-72" data-id="element-107" data-mp="1zwuh7is6tfem">
                <img src={IMAGES.guiaSos} alt="Guía SOS" className="max-w-full max-h-full object-contain drop-shadow-xl" data-id="element-108" data-mp="24pxbjt4sgsvv" />
              </div>
              <div className="p-6 md:p-8 flex-1 border-t border-travertino/10" data-id="element-109" data-mp="2dq3j6ydt2yn4">
                <div className="flex items-center gap-2 mb-3" data-id="element-110" data-mp="2do8ztoi6nel5">
                  <Smartphone size={18} className="text-caribe" data-id="element-111" data-mp="2ipyf5gcfuef1" />
                  <span className="text-xs font-bold text-caribe uppercase tracking-wider" data-id="element-112" data-mp="251xyzncmsmu7">
                    Recurso rápido
                  </span>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-2" data-id="element-113" data-mp="20z1luc5v1ixn">
                  Guía SOS para leer en el avión
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-114" data-mp="29uhb0fvo79ww">
                  Un recurso rápido para abrir desde el celular cuando aparece
                  ansiedad, turbulencia, miedo al despegue o sensación de
                  pérdida de control.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-3xl overflow-hidden border border-travertino/20 shadow-sm flex flex-col" data-id="element-115" data-mp="1vzf1zjvj14fv">
              <div className="p-6 bg-polar flex justify-center items-center h-64 md:h-72" data-id="element-116" data-mp="37gf1p0k7eljx">
                <img src={IMAGES.biblioteca} alt="Biblioteca de Calma en Vuelo" className="max-w-full max-h-full object-contain drop-shadow-xl rounded-xl" data-id="element-117" data-mp="32i3qomi6a504" />
              </div>
              <div className="p-6 md:p-8 flex-1 border-t border-travertino/10" data-id="element-118" data-mp="ocfb2fr6uhef">
                <div className="flex items-center gap-2 mb-3" data-id="element-119" data-mp="tagskw8yu8ns">
                  <Headphones size={18} className="text-caribe" data-id="element-120" data-mp="fa639xnb124q" />
                  <span className="text-xs font-bold text-caribe uppercase tracking-wider" data-id="element-121" data-mp="k8hcq79otamf">
                    Audio
                  </span>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-2" data-id="element-122" data-mp="17ylbo1bxkm73">
                  Biblioteca de Calma en Vuelo
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-123" data-mp="y8s3zbewsr84">
                  Incluye audios de soporte, meditaciones guiadas y podcasts
                  explicativos para acompañarte antes y durante el vuelo.
                </p>
              </div>
            </div>

            {/* Card 4 */}
            <div className="bg-white rounded-3xl overflow-hidden border border-travertino/20 shadow-sm flex flex-col md:col-start-1 lg:col-start-2" data-id="element-124" data-mp="1vnw6z3aihodu">
              <div className="p-6 bg-polar flex justify-center items-center h-64 md:h-72" data-id="element-125" data-mp="25hjy7fz2b09m">
                <img src={IMAGES.cuaderno} alt="Cuaderno" className="max-w-full max-h-full object-contain drop-shadow-xl" data-id="element-126" data-mp="20lbavfapo0rd" />
              </div>
              <div className="p-6 md:p-8 flex-1 border-t border-travertino/10" data-id="element-127" data-mp="1qmm1yc0slqn9">
                <div className="flex items-center gap-2 mb-3" data-id="element-128" data-mp="1mks6qj9d9euh">
                  <FileText size={18} className="text-caribe" data-id="element-129" data-mp="1mmblu6jadjuo" />
                  <span className="text-xs font-bold text-caribe uppercase tracking-wider" data-id="element-130" data-mp="1htsalg82n0t1">
                    Imprimible
                  </span>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-2" data-id="element-131" data-mp="17vw1etyp2fxk">
                  Cuaderno de ejercicios imprimible +30 páginas
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-132" data-mp="12vzv2o2iyceu">
                  Ejercicios para ordenar tus pensamientos, identificar miedos,
                  preparar tu plan de calma y registrar tu avance.
                </p>
              </div>
            </div>

            {/* Card 5 */}
            <div className="bg-white rounded-3xl overflow-hidden border border-travertino/20 shadow-sm flex flex-col" data-id="element-133" data-mp="3eibk2uky9jj0">
              <div className="p-6 bg-polar flex justify-center items-center h-64 md:h-72" data-id="element-134" data-mp="3jheqaico1vsk">
                <img src={IMAGES.checklist} alt="Checklist" className="max-w-full max-h-full object-contain drop-shadow-xl" data-id="element-135" data-mp="3og81230kj8p1" />
              </div>
              <div className="p-6 md:p-8 flex-1 border-t border-travertino/10" data-id="element-136" data-mp="2vsvie3xj3l10">
                <div className="flex items-center gap-2 mb-3" data-id="element-137" data-mp="30szyhsuiydqp">
                  <ListChecks size={18} className="text-caribe" data-id="element-138" data-mp="30qn7yrapav21" />
                  <span className="text-xs font-bold text-caribe uppercase tracking-wider" data-id="element-139" data-mp="35khxibczm491">
                    Organización
                  </span>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-2" data-id="element-140" data-mp="2d2m4hwsdq4th">
                  Checklist pre-vuelo antiansiedad
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-141" data-mp="16hxjlkg8ibdc">
                  Una lista práctica para preparar documentos, objetos de calma,
                  audios, respiraciones y todo lo necesario antes de subir al
                  avión.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECCIÓN “CUÁNDO USARLO” */}
      <section className="py-16 md:py-20 px-4 bg-polar" data-id="element-142" data-mp="1pcuq52uns4oe">
        <div className="max-w-4xl mx-auto" data-id="element-143" data-mp="1kge1m7jjroas">
          <h2 className="font-cormorant text-3xl md:text-5xl font-semibold text-oceano text-center mb-12" data-id="element-144" data-mp="1kfcfn80zthea">
            Usalo justo cuando más lo necesitás
          </h2>

          <div className="space-y-6 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-travertino/30" data-id="element-145" data-mp="1u6gvsb2en8ap">
            {[{
            title: 'Antes del viaje',
            desc: 'Para ansiedad anticipatoria, pensamientos repetitivos y miedo antes de dormir.'
          }, {
            title: 'En el aeropuerto',
            desc: 'Para bajar nervios, ordenar la mente y no entrar al avión en alerta total.'
          }, {
            title: 'Durante el despegue',
            desc: 'Para respirar, entender los ruidos y atravesar los primeros minutos.'
          }, {
            title: 'En turbulencia',
            desc: 'Para recordar qué hacer, escuchar audios y evitar entrar en pánico.'
          }, {
            title: 'En pleno vuelo',
            desc: 'Para volver al presente cuando aparece sensación de encierro o pérdida de control.'
          }].map((item, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-146" data-mp="1z21klpe0kms2" data-mpi={i}>
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-polar bg-caribe text-polar shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10" data-id="element-147" data-mp="1l31zgrppmq0n">
                  <Check size={20} data-id="element-148" data-mp="fdh3bj9zv0yc" />
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-5 rounded-2xl shadow-sm border border-travertino/20" data-id="element-149" data-mp="kb0gsgyvb9p8">
                  <h3 className="font-cormorant text-xl font-semibold text-oceano mb-1" data-id="element-150" data-mp="6csoimcs0wke">
                    {item.title}
                  </h3>
                  <p className="text-hierro text-sm leading-relaxed" data-id="element-151" data-mp="y1caulf0jij0">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="py-16 md:py-20 px-4 bg-oceano text-polar" data-id="element-152" data-mp="p91yoykk52z4">
        <div className="max-w-4xl mx-auto" data-id="element-153" data-mp="t9u9vxczphy1">
          <h2 className="font-cormorant text-3xl md:text-5xl font-semibold text-center mb-10" data-id="element-154" data-mp="t8t0dxbmh3if">
            Con este método vas a poder…
          </h2>

          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mb-12" data-id="element-155" data-mp="1ls8a23lstg0a">
            {['Llegar al vuelo con un plan, no solo con miedo.', 'Entender sensaciones que antes te asustaban.', 'Tener una guía para leer en el avión.', 'Escuchar audios cuando necesitás calma.', 'Prepararte emocionalmente antes de viajar.', 'Sentirte más acompañada/o durante el vuelo.'].map((benefit, i) => <div key={i} className="flex items-start gap-3 bg-polar/5 p-4 rounded-xl border border-polar/10" data-id="element-156" data-mp="3b8lomalti36w" data-mpi={i}>
                <CheckCircle2 className="text-caribe shrink-0 mt-0.5" size={20} data-id="element-157" data-mp="2ni7vegrhxyaq" />
                <span className="text-polar/90 font-medium" data-id="element-158" data-mp="2ijo9uhgteoiw">{benefit}</span>
              </div>)}
          </div>

          <div className="text-center" data-id="element-159" data-mp="2xhgkowbf2oqr">
            <p className="font-cormorant text-2xl md:text-3xl text-travertino italic" data-id="element-160" data-mp="2xii9s4p9pbrh">
              "No prometemos magia. Te damos algo más útil: herramientas
              concretas para atravesar el miedo."
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="py-16 md:py-20 px-4 bg-[#F5F5F0] overflow-hidden" data-id="element-161" data-mp="7qtt4m8h313y">
        <div className="max-w-6xl mx-auto" data-id="element-162" data-mp="evl793xon6br">
          <div className="text-center mb-12" data-id="element-163" data-mp="eww5ypc9tnw0">
            <h2 className="font-cormorant text-3xl md:text-5xl font-semibold text-oceano mb-4" data-id="element-164" data-mp="jt3gkvfm3nka">
              Personas que empezaron a viajar con más calma
            </h2>
          </div>

          <TestimonialCarousel data-id="element-165" data-mp="tslyl6byoz9p" />
        </div>
      </section>

      {/* SECCIÓN DE COMPRA */}
      <section id="compra" className="py-16 md:py-24 px-4 bg-oceano relative" data-id="element-166" data-mp="3s9y8xgys9pk4">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-167" data-mp="3s7vbibvawu5d"></div>

        <div className="max-w-5xl mx-auto relative z-10" data-id="element-168" data-mp="61yelmoruj6s">
          <div className="text-center mb-10" data-id="element-169" data-mp="60exjtvak43y">
            <h2 className="font-cormorant text-4xl md:text-5xl lg:text-6xl font-semibold text-polar mb-4" data-id="element-170" data-mp="atgahwbxttkw">
              Accedé hoy al Método Volar en Calma
            </h2>
          </div>

          <div className="bg-polar rounded-3xl p-6 md:p-12 shadow-2xl max-w-3xl mx-auto" data-id="element-171" data-mp="3ehrpnm0c32gg">
            {/* 1. Todo lo que incluye (primero y llamativo) */}
            <div className="mb-10" data-id="element-172" data-mp="1vl2pv20g3e7l">
              <h3 className="font-cormorant text-2xl md:text-3xl font-semibold text-oceano mb-6 text-center" data-id="element-173" data-mp="1rl22grvdbsoe">
                Todo lo que incluye el método
              </h3>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3" data-id="element-174" data-mp="1mobjnws6iiza">
                {['Ebook principal Aerofobia: Guía para Volar en Calma', 'Guía SOS para leer en el avión', 'Audios de soporte para momentos de ansiedad', 'Meditaciones guiadas para viajar con calma', 'Podcasts explicativos para entender el miedo a volar', 'Cuaderno de ejercicios imprimible +30 páginas', 'Checklist pre-vuelo antiansiedad'].map((item, i) => <li key={i} className="flex items-start gap-3 text-oceano font-medium text-sm md:text-base" data-id="element-175" data-mp="ywwdfsxa7hir" data-mpi={i}>
                    <CheckCircle2 size={20} className="text-caribe shrink-0 mt-0.5" data-id="element-176" data-mp="tyctfy290ft1" />
                    <span data-id="element-177" data-mp="oyqixwdt1dnx">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* 2. Oferta especial por tiempo limitado y compra */}
            <div className="flex flex-col items-center text-center bg-white p-6 md:p-10 rounded-3xl border-2 border-travertino/20 shadow-sm" data-id="element-178" data-mp="2p1rl0kv4ovse">
              <div className="bg-red-50 text-red-600 px-4 py-2 rounded-full text-xs md:text-sm font-bold mb-6 flex items-center gap-2 animate-pulse" data-id="element-179" data-mp="2fbg9ac57diay">
                <Clock size={16} data-id="element-180" data-mp="2k8gdehihsazf" />
                Oferta especial por tiempo limitado
              </div>

              {isPriceLoading ? <div className="mb-6 flex flex-col items-center gap-3" aria-live="polite" aria-busy="true" data-id="element-181" data-mp="26cuqqgzqe3a6">
                  <div className="h-6 w-48 rounded-full bg-travertino/20 animate-pulse" data-id="element-182" data-mp="1wcc278x81ylx"></div>
                  <div className="h-12 md:h-14 w-56 rounded-xl bg-travertino/20 animate-pulse" data-id="element-183" data-mp="21bf6us9ai2td"></div>
                  <span className="sr-only" data-id="element-184" data-mp="3qqbu6fug0fzy">Calculando el precio para tu país…</span>
                </div> : <>
                  <div className="mb-1" data-id="element-185" data-mp="3vp6fbofvugn8">
                    <span className="text-hierro line-through text-lg md:text-xl" data-id="element-186" data-mp="3vowk9mfzdjao">
                      Precio anterior: {price.originalPrice}
                    </span>
                  </div>
                  <div className="mb-6" data-id="element-187" data-mp="1f4b0h6fqtdlf">
                    <span className="text-caribe font-bold text-4xl md:text-6xl" data-id="element-188" data-mp="1f15fo3e5cxvi">
                      {price.price}
                    </span>
                  </div>
                </>}

              <div className="w-full max-w-md space-y-4" data-id="element-189" data-mp="1oz30si0pmejw">
                <button onClick={buyNow} className="w-full bg-caribe hover:bg-[#24a8a2] text-polar py-4 md:py-5 rounded-xl font-bold text-lg md:text-xl transition-colors shadow-xl shadow-caribe/20" data-id="element-190" data-mp="22rja20k7f8t9">
                  QUIERO ACCEDER
                </button>

                <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-[11px] md:text-xs text-hierro font-medium pt-2" data-id="element-191" data-mp="27rnozm5grvhd">
                  <span className="flex items-center gap-1" data-id="element-192" data-mp="idik07owdjj4">
                    <ShieldCheck size={14} data-id="element-193" data-mp="ddw8q3smqdcy" /> Pago seguro
                  </span>
                  <span className="flex items-center gap-1" data-id="element-194" data-mp="dfz83efo9iuc">
                    <Download size={14} data-id="element-195" data-mp="2dhglpp16rjly" /> Acceso inmediato
                  </span>
                  <span className="flex items-center gap-1" data-id="element-196" data-mp="2ig07npfs4veb">
                    <Smartphone size={14} data-id="element-197" data-mp="2mgk6qyjwy933" /> 100% digital
                  </span>
                </div>
              </div>

              <p className="text-xs md:text-sm text-hierro mt-6 pt-6 border-t border-travertino/20" data-id="element-198" data-mp="2rfx3qc4i0wje">
                Recibís el acceso al contenido digital para empezar a usarlo
                antes de tu próximo vuelo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 px-4 bg-[#F5F5F0]" data-id="element-199" data-mp="365maqnz9vurl">
        <div className="max-w-3xl mx-auto" data-id="element-200" data-mp="3a5wpap14rti2">
          <h2 className="font-cormorant text-3xl md:text-5xl font-semibold text-oceano text-center mb-10" data-id="element-201" data-mp="3a4v3bpiktmlk">
            Preguntas frecuentes
          </h2>

          <div className="space-y-3" data-id="element-202" data-mp="3k42o2xrn4jip">
            {FAQS.map((faq, i) => <div key={i} className="bg-white rounded-2xl border border-travertino/20 overflow-hidden" data-id="element-203" data-mp="3k4lvhkjnzpih" data-mpi={i}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-5 py-4 text-left flex justify-between items-center focus:outline-none" data-id="element-204" data-mp="3u66dsqp7kjyd">
                  <span className="font-semibold text-oceano pr-4 text-base md:text-lg" data-id="element-205" data-mp="3kauwq1qbv0ng">
                    {faq.q}
                  </span>
                  <ChevronRight className={`text-caribe shrink-0 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} size={20} data-id="element-206" data-mp="3gauf687xm9c6" />
                </button>
                {openFaq === i && <div className="px-5 pb-4 text-hierro text-sm md:text-base leading-relaxed" data-id="element-207" data-mp="bv1oea65gvhh">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* CIERRE FINAL */}

      {/* Footer */}
      <footer className="bg-oceano text-polar/60 py-12 px-4 text-sm text-center border-t border-polar/10" data-id="element-208" data-mp="zk44hdl4yg9v">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-209" data-mp="us2w42r399p4">
          <div className="inline-block bg-polar rounded-2xl px-6 py-4 shadow-sm" data-id="element-210" data-mp="1eh68baieg5a0">
            <img src={IMAGES.logo} alt="Atlas de Viajes" className="h-14 md:h-16 mx-auto object-contain" data-id="element-211" data-mp="1ef39q236l3to" />
          </div>
          <p className="leading-relaxed" data-id="element-212" data-mp="14j05mqorqv43">
            Este producto es un recurso educativo y de acompañamiento. No
            reemplaza atención médica, psicológica ni tratamiento profesional.
            Si tu ansiedad es muy intensa, frecuente o limita tu vida diaria,
            recomendamos consultar con un profesional de salud mental.
          </p>
          <p className="pt-4 border-t border-polar/10" data-id="element-213" data-mp="2268r4lors463">
            © {new Date().getFullYear()} Atlas de Viajes. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>
    </div>;
}